import React from 'react';
import {
  ProductTabsNavigation,
  ProductWrapper,
  CmsCard,
  CmsGrid,
  CmsKpiGrid,
  CmsKpiTile,
  CustomReactComponent,
  CmsDataDefinitionIcon,
  CmsLineChart,
  CmsBarChart,
  CmsHorizontalProgressBarList,
  CmsPieChart,
  CmsListV2,
  CmsGaugeChart,
  CmsSwitcher,
} from './components';
// eslint-disable-next-line no-duplicate-imports
import type { RegistryEntry, ComponentRegistry } from './components';

/**
 * CMS Component Registry
 *
 * Maps DatoCMS _modelApiKey values to React components with transformation layer.
 * Components are lazy loaded for code splitting.
 *
 * Each registry entry defines:
 * - Component: The React component to render (lazy loaded)
 * - transform: Function to map CMS props to component props
 * - childrenKey: Property name containing child nodes
 * - Wrapper: Optional wrapper for state/context management
 */

const componentRegistry: ComponentRegistry = {
  /**
   * Product - Container with tabs navigation
   * Note: We pass tabs directly to the component instead of using childrenKey
   * because pepsico-ds TabsNavigation requires Tab components as direct children
   * Supports both direct tabs (product.tabs) and model-based tabs (product.models[].tabs)
   */
  product: {
    Component: ProductTabsNavigation,
    transform: (node) => ({
      id: node.id,
      title: node.title,
      tabs: (node.tabs as unknown[]) || [],
      models: (node.models as unknown[]) || [],
    }),
    Wrapper: ProductWrapper,
  },

  /**
   * Tab - Individual tab item
   * Note: Tabs have content array which contains Grid components
   */
  tab: {
    Component: ({ children }: { children?: React.ReactNode }) => (
      <>{children}</>
    ),
    transform: (node) => ({
      id: node.id,
      label: node.title || node.slug || 'Untitled Tab',
      slug: node.slug,
    }),
    childrenKey: 'content',
  },

  /**
   * Card - CardV2 component
   * Supports headerContent for adding elements to the card header (e.g., data definition icon)
   * Children come from both 'content' (KpiGrid, etc.) and 'blocks' (LineChart, etc.)
   */
  card: {
    Component: CmsCard,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      heading: node.heading,
      nested: node.nested,
      headerContent: node.headerContent || [],
    }),
    childrenKey: ['content', 'blocks'],
  },

  /**
   * Grid - Responsive grid layout with configurable columns
   * Children come from both 'blocks' (cards, components) and 'content' (nested grids)
   */
  grid: {
    Component: CmsGrid,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      columnsMobile: node.columnsMobile,
      columnsTablet: node.columnsTablet,
      columnsDesktop: node.columnsDesktop,
      gap: node.gap,
      padding: node.padding,
      margin: node.margin,
      backgroundColor: node.backgroundColor,
      renderOrder: node.renderOrder, // Controls render order: 'content_first' or 'blocks_first'
      blocks: node.blocks || [], // Raw CMS nodes for internal rendering
      content: node.content || [], // Raw CMS nodes for internal rendering
    }),
    // Note: No childrenKey - CmsGrid handles internal rendering with order logic
  },

  /**
   * KPI Grid - Grid layout for KPI tiles with responsive columns and dividers
   *
   * Uses config JSON for all grid settings with defaults:
   * {
   *   "columnsMobile": 2,
   *   "columnsTablet": 5,
   *   "columnsDesktop": 5,
   *   "showBorderMobile": true,
   *   "showBorderTablet": true,
   *   "showBorderDesktop": true,
   *   "showHDividerMobile": true,
   *   "showHDividerTablet": true,
   *   "showHDividerDesktop": true,
   *   "showHDividerLastRow": false
   * }
   */
  kpi_grid: {
    Component: CmsKpiGrid,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      config: node.config,
    }),
    childrenKey: 'tiles',
  },

  /**
   * KPI Tile - Individual KPI display with value and tags
   *
   * Supports two modes:
   * 1. Legacy: Uses valueDataKey, valueType, and tags with textDataKey
   * 2. Config: Uses config.values array and config.template for flexible composition
   *
   * Config mode example:
   * {
   *   "values": [{ "slug": "val", "dataKey": "getData.value", "format": "number" }],
   *   "template": "{{val}}",
   *   "tags": [{ "template": "{{delta}}", "valueSlug": "delta", "sideLabel": "vs LY" }]
   * }
   */
  kpi_tile: {
    Component: CmsKpiTile,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      title: node.title,
      secondTitle: node.secondTitle,
      subTitle: node.subtitle, // CMS uses 'subtitle', component uses 'subTitle'
      // Legacy props
      valueDataKey: node.valueDataKey,
      valueType: node.valueType,
      tags: (node.tags as Array<Record<string, unknown>> | undefined)?.map(
        (tag: Record<string, unknown>) => ({
          textDataKey: tag.textDataKey,
          valueType: tag.valueType,
          sideLabel: tag.sideLabel,
          showArrow: tag.showArrow,
          arrowReversed: tag.arrowReversed,
        })
      ),
      // New config-based approach
      config: node.config,
    }),
  },

  /**
   * Custom React Component - Resolves component from slug registry
   */
  custom_react_component: {
    Component: CustomReactComponent,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      reactComponentSlug: node.reactComponentSlug,
      availabilityRules: node.availabilityRules,
    }),
  },

  /**
   * Data Definition - Standalone info icon that opens data definition drawer
   * Can be used as block content or within card headerContent
   * Uses a single JSON config field containing title, subHeading, and sections
   */
  data_definition: {
    Component: CmsDataDefinitionIcon,
    transform: (node) => ({
      config: node.config,
    }),
  },

  /**
   * Line Chart - Configurable line chart (single or multi-line)
   *
   * Supports:
   * - Single line and multi-line charts via chartType
   * - Dynamic templates for titles, series names, tooltips
   * - Configurable axis formatting, grid margins, legend
   *
   * Config example:
   * {
   *   "dataKey": "getData.trend",
   *   "chartType": "single",
   *   "xAxis": { "field": "dateTime", "format": "date" },
   *   "yAxis": { "format": "percentage" },
   *   "series": [{ "name": "${lineName}", "field": "value", "color": "#33B1E8" }]
   * }
   */
  line_chart: {
    Component: CmsLineChart,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },

  /**
   * Bar Chart - CMS-driven bar/multi-bar chart
   *
   * Config example:
   * {
   *   "dataKey": "getData.userTrend",
   *   "chartType": "single",
   *   "xAxis": { "field": "dateTime", "format": "date", "dateFormat": "monthly" },
   *   "yAxis": { "format": "number" },
   *   "series": [
   *     { "name": "Total Users", "field": "totalUser", "color": "#A35079" },
   *     { "name": "Active Users", "field": "activeUser", "color": "#33B1E8" }
   *   ],
   *   "legend": { "show": true, "bottom": "5%", "right": "3%" }
   * }
   */
  bar_chart: {
    Component: CmsBarChart,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },

  /**
   * Horizontal Progress Bar List - List of items with horizontal progress bars
   *
   * Displays progress bars showing percentages or values with configurable styling.
   * Supports scrolling for long lists and custom value formatting.
   *
   * Config example:
   * {
   *   "dataKey": "getData.adoptionByMarket",
   *   "displayMode": "percentage",
   *   "wrapText": false,
   *   "fixedHeight": 300,
   *   "equalVerticalSpacing": false,
   *   "fields": {
   *     "title": "marketName",
   *     "color": "barColor",
   *     "percentage": "adoptionRate"
   *   }
   * }
   */
  horizontal_progress_bar_list: {
    Component: CmsHorizontalProgressBarList,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },

  /**
   * Pie Chart - CMS-driven pie/donut chart
   *
   * Supports:
   * - Static segments defined in config.segments
   * - Dynamic segments from API via dataKey/dataArrayPath
   * - Custom colors per segment or colorPalette
   * - Labels with percentage display
   * - Donut variant via radius: ["40%", "70%"]
   *
   * Config example (static segments):
   * {
   *   "segments": [
   *     { "label": "Success", "value": 75, "color": "#007EB5" },
   *     { "label": "Failed", "value": 25, "color": "#A35079" }
   *   ],
   *   "showPercentage": true,
   *   "legend": { "show": true, "position": "bottom" }
   * }
   *
   * Config example (dynamic data):
   * {
   *   "dataKey": "getData.distribution",
   *   "dataArrayPath": "segments",
   *   "labelField": "category",
   *   "valueField": "count",
   *   "colorPalette": ["#33ABDA", "#D34D70", "#0078A7"]
   * }
   */
  pie_chart: {
    Component: CmsPieChart,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },

  /**
   * List V2 - CMS-driven ListV2 component from pepsico-ds
   *
   * Displays a list of items with configurable presentation and field mappings.
   * Supports dynamic data from GraphQL with automatic transformation.
   *
   * Config example:
   * {
   *   "dataKey": "getData.topMetrics",
   *   "presentation": "border",
   *   "type": "navigation",
   *   "separator": true,
   *   "draggable": false,
   *   "fields": {
   *     "title": "metricName",
   *     "id": "metricId",
   *     "subtitle": "category",
   *     "description": "details"
   *   }
   * }
   */
  list_v2: {
    Component: CmsListV2,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },

  /**
   * Gauge Chart - CMS-driven semi-circular gauge chart
   *
   * Displays a gauge with configurable min/max range, colors, and value formatting.
   * Supports dynamic data from GraphQL with automatic value resolution.
   *
   * Config example:
   * {
   *   "dataKey": "getData.score",
   *   "min": 0,
   *   "max": 100,
   *   "title": "Performance Score",
   *   "suffix": "%",
   *   "gaugeColor": "#007EB5",
   *   "arcWidth": 50,
   *   "showMinMaxLabels": true
   * }
   */
  gauge_chart: {
    Component: CmsGaugeChart,
    transform: (node) => ({
      id: node.id,
      config: node.config,
    }),
  },
  /**
   * Switcher - Tab-like component for toggling between content views
   *
   * Supports:
   * - headerContent: Link to CustomReactComponent for header filter/controls
   * - items: Linked SwitcherItem records with availabilityRules for conditional disablement
   * - contentSlug: Each item links to a Grid for its content
   *
   * DatoCMS Structure:
   * - Switcher (Collection Model) with headerContent link to CustomReactComponent
   * - SwitcherItem (Collection Model) with availabilityRules JSON for market-based disablement
   */
  switcher: {
    Component: CmsSwitcher,
    transform: (node) => ({
      id: node.id,
      slug: node.slug,
      size: node.size,
      defaultIndex: node.defaultIndex,
      headerContent: node.headerContent, // Link to CustomReactComponent
      items: node.items, // Linked SwitcherItem records with availabilityRules
    }),
    childrenKey: 'blocks', // Legacy: Content blocks to show/hide based on selection
  },
};

// ─── REGISTRY API ─────────────────────────────────────────────────────────

/**
 * Get registry entry for a given _modelApiKey
 */
export const getRegistryEntry = (modelApiKey: string): RegistryEntry | null => {
  return componentRegistry[modelApiKey] || null;
};

/**
 * Check if a component exists for a given _modelApiKey
 */
export const hasComponent = (modelApiKey: string): boolean => {
  return modelApiKey in componentRegistry;
};

/**
 * Register a new component for a _modelApiKey
 */
export const registerComponent = (
  modelApiKey: string,
  entry: RegistryEntry
): void => {
  componentRegistry[modelApiKey] = entry;
};

/**
 * Get all registered _modelApiKey values
 */
export const getRegisteredKeys = (): string[] => {
  return Object.keys(componentRegistry);
};

// Re-export types
export type { CmsNode, RegistryEntry, CmsWrapperProps } from './components';

export default componentRegistry;
